import type { ReactElement } from "react";
import { KeyRound } from "lucide-react";
import { SettingsGroup, TabPanel, Tabs } from "@playbookai/ui";
import { AdminSection } from "./AdminSection.js";
import { ScimOAuthClientsPanel } from "./ScimOAuthClientsPanel.js";
import { IdentityOverviewTab } from "./IdentityOverviewTab.js";
import { IdentityTeamsTab } from "./IdentityTeamsTab.js";
import { IdentityActivityTab } from "./IdentityActivityTab.js";

/**
 * Identity & Directory is an operational workspace, not a settings index.
 * Four task-focused tabs replace the old long scroll and permanent right rail.
 * Directory groups remain directory data; Shared Teams remain the Auth-owned
 * accepted-member aggregate defined by ADR-0089.
 */
export function IdentityAdmin(): ReactElement {
  return (
    <AdminSection
      icon={KeyRound}
      title="Identity & directory"
      description="Manage sign-in provisioning, understand directory health, and monitor shared Teams and identity activity."
      meta={null}
    >
      <Tabs
        items={[
          { value: "overview", label: "Overview" },
          { value: "provisioning", label: "Provisioning" },
          { value: "teams", label: "Teams" },
          { value: "activity", label: "Activity" },
        ]}
        defaultValue="overview"
        aria-label="Identity and directory sections"
      >
        <TabPanel value="overview" padding="md">
          <IdentityOverviewTab />
        </TabPanel>

        <TabPanel value="provisioning" padding="md">
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-pb-text">
                SCIM provisioning
              </h2>
              <p className="mt-1 text-sm text-pb-text-secondary">
                Create and manage OAuth clients used by your identity provider
                to provision people and directory groups.
              </p>
            </div>
            <SettingsGroup
              title="OAuth clients"
              description="Secrets are shown once. Copy the complete configuration before closing the confirmation."
            >
              <ScimOAuthClientsPanel />
            </SettingsGroup>
          </div>
        </TabPanel>

        <TabPanel value="teams" padding="md">
          <IdentityTeamsTab />
        </TabPanel>

        <TabPanel value="activity" padding="md">
          <IdentityActivityTab />
        </TabPanel>
      </Tabs>
    </AdminSection>
  );
}
